/**
 * Gerenciador de Fila de Mensagens
 * 
 * Responsável por:
 * - Enfileirar mensagens quando sessionToken não está disponível
 * - Fazer flush automático quando token chega
 * - Gerenciar timeouts de fila
 * - Validar tamanho de payloads antes de enviar
 * 
 * @module MessageQueue
 */

import { WebViewResponse } from '../../types/messages';
import { sendToTerminal } from '../../../terminalLogger';
import { postJsonToWebView } from '../../transport';
import { LogCategory } from '@/config/logging';

export interface QueueItem {
  action: string;
  data: Record<string, any>;
  requestId: string;
  callback?: (response: WebViewResponse) => void;
  timeoutId?: number;
}

/**
 * Gerenciador de fila para mensagens pendentes
 */
export class MessageQueue {
  private queue: QueueItem[] = [];
  private readonly QUEUE_TIMEOUT_MS = 30000; // 30 segundos (alinhado com tempo razoável de bootstrap)
  private readonly MAX_PAYLOAD_BYTES = 102400; // 100 KB

  /**
   * Enfileira uma mensagem aguardando sessionToken
   * 
   * @param item Item a ser enfileirado
   */
  public enqueue(item: Omit<QueueItem, 'timeoutId'>): void {
    const timeoutId = window.setTimeout(() => {
      this.handleTimeout(item.requestId);
    }, this.QUEUE_TIMEOUT_MS);

    this.queue.push({
      ...item,
      timeoutId
    });

    sendToTerminal('📥 [MESSAGE_QUEUE] Mensagem enfileirada', {
      action: item.action,
      requestId: item.requestId,
      queueSize: this.queue.length
    }, LogCategory.QUEUE);
  }

  /**
   * Remove e retorna todas as mensagens da fila (sem notificar callbacks)
   */
  private dequeueAll(): QueueItem[] {
    const items = [...this.queue];
    this.queue = [];
    return items;
  }
  
  /**
   * Remove e retorna todas as mensagens da fila para reprocessamento
   * 
   * ✅ NOVO (2025): Limpa timeouts mas NÃO notifica callbacks de cancelamento,
   * permitindo que as mensagens sejam reprocessadas com autenticação HMAC.
   * 
   * @returns Itens da fila para reprocessamento
   */
  public dequeueForReprocessing(): QueueItem[] {
    const items = this.dequeueAll();
    
    // Limpar timeouts sem notificar callbacks
    for (const item of items) {
      if (item.timeoutId) {
        try {
          clearTimeout(item.timeoutId);
        } catch (error) {
          // Ignorar erros ao limpar timeout
        }
      }
    }
    
    if (items.length > 0) {
      sendToTerminal('🔄 [MESSAGE_QUEUE] Itens removidos para reprocessamento', {
        itemCount: items.length
      }, LogCategory.QUEUE);
    }
    
    return items;
  }

  /**
   * Processa timeout de uma mensagem na fila
   */
  private handleTimeout(requestId: string): void {
    const idx = this.queue.findIndex(q => q.requestId === requestId);
    if (idx >= 0) {
      const pending = this.queue.splice(idx, 1)[0];
      if (pending?.callback) {
        try {
          pending.callback({
            requestId,
            status: 'erro',
            message: 'Token de sessão indisponível'
          } as any);
        } catch (error) {
          sendToTerminal('❌ [MESSAGE_QUEUE] Erro no callback de timeout', {
            requestId,
            error: error instanceof Error ? error.message : String(error)
          }, LogCategory.ERROR);
        }
      }
    }
  }

  /**
   * Flush da fila: envia todas as mensagens pendentes
   * 
   * @param sessionToken Token de sessão a ser injetado
   */
  public flush(sessionToken: string): void {
    if (!sessionToken) {
      sendToTerminal('⚠️ [MESSAGE_QUEUE] Flush ignorado - sem token', { reason: 'No session token' }, LogCategory.QUEUE);
      return;
    }

    const items = this.dequeueAll();
    
    // ✅ Log apenas quando há itens na fila
    if (items.length > 0) {
      sendToTerminal('🔄 [MESSAGE_QUEUE] Fazendo flush da fila', {
        itemCount: items.length,
        hasToken: !!sessionToken
      }, LogCategory.QUEUE);
    }

    for (const item of items) {
      try {
        // Injetar sessionToken nos dados
        const dataWithToken = {
          sessionToken,
          ...item.data
        };

        // Montar mensagem completa
        const message = {
          action: item.action,
          data: dataWithToken,
          requestId: item.requestId
        };

        // Validar tamanho
        const jsonPayload = JSON.stringify(message);
        if (jsonPayload.length > this.MAX_PAYLOAD_BYTES) {
          sendToTerminal('❌ [MESSAGE_QUEUE] Payload muito grande - descartado', {
            action: item.action,
            requestId: item.requestId,
            size: jsonPayload.length
          }, LogCategory.ERROR);

          // Notificar callback de erro
          if (item.callback) {
            try {
              item.callback({
                requestId: item.requestId,
                status: 'erro',
                message: 'Payload muito grande'
              } as any);
            } catch (error) {
              sendToTerminal('❌ [MESSAGE_QUEUE] Erro no callback', {
                requestId: item.requestId,
                error: error instanceof Error ? error.message : String(error)
              }, LogCategory.ERROR);
            }
          }
          continue;
        }

        // Enviar
        postJsonToWebView(jsonPayload);

        sendToTerminal('📤 [MESSAGE_QUEUE] Mensagem enviada', {
          action: item.action,
          requestId: item.requestId
        }, LogCategory.QUEUE);
      } catch (error) {
        sendToTerminal('❌ [MESSAGE_QUEUE] Erro ao enviar mensagem', {
          action: item.action,
          requestId: item.requestId,
          error: error instanceof Error ? error.message : String(error)
        }, LogCategory.ERROR);
      } finally {
        // Limpar timeout
        if (item.timeoutId) {
          try {
            clearTimeout(item.timeoutId);
          } catch (error) {
            // Ignorar erros ao limpar timeout
          }
        }
      }
    }
  }

  /**
   * Limpa toda a fila (cancela todas as mensagens pendentes)
   */
  public clear(): void {
    const items = this.dequeueAll();
    
    // Cancelar todos os timeouts
    for (const item of items) {
      if (item.timeoutId) {
        try {
          clearTimeout(item.timeoutId);
        } catch (error) {
          // Ignorar erros ao limpar timeout
        }
      }

      // Notificar callbacks de cancelamento
      if (item.callback) {
        try {
          item.callback({
            requestId: item.requestId,
            status: 'erro',
            message: 'Fila cancelada'
          } as any);
        } catch (error) {
          sendToTerminal('❌ [MESSAGE_QUEUE] Erro no callback de limpeza', {
            requestId: item.requestId,
            error: error instanceof Error ? error.message : String(error)
          }, LogCategory.ERROR);
        }
      }
    }

    sendToTerminal('🧹 [MESSAGE_QUEUE] Fila limpa', { itemCount: items.length }, LogCategory.QUEUE);
  }

  /**
   * Retorna o tamanho atual da fila
   */
  public size(): number {
    return this.queue.length;
  }

  /**
   * Verifica se a fila está vazia
   */
  public isEmpty(): boolean {
    return this.queue.length === 0;
  }
}

